import React from "react"

// threeJS stuff
import * as THREE from "three"
import { OrbitControls, OBJLoader } from "three-stdlib"

import PropTypes from "prop-types"
import { notGatsby, graphql } from "gatsby"
import styled from "styled-components"
import { shadow, color, compose } from "styled-system"
import css from "../../lib/styled-system/css"

import ThemeSwitcher from "../utils/ThemeSwitcher"
import Link from "../utils/Link"
import Image from "../utils/Image"

import Flex from "../atoms/Flex"
import Box from "../atoms/Box"
import { Title } from "../atoms/Typography"
import useBreakpoint from "../../hooks/use-breakpoint"

export const StyledHeader = styled("header")(
  // Static styles
  { position: "relative" },
  // Dynamic styles that can be changed by props
  compose(color, shadow)
)

class ThreeScene extends React.Component {
  constructor(props) {
    super(props)
    this.state = { objScale: 0.013 }
    // this.handleResize = this.handleResize.bind(this);
  }
  componentDidMount() {
    const width = !this.props.isHomepage
      ? 70
      : this.props.isMobileBp && window.innerHeight > window.innerWidth
      ? window.innerWidth
      : window.innerHeight / 1.9
    const height = !this.props.isHomepage
      ? 70
      : this.props.isMobileBp && window.innerHeight > window.innerWidth
      ? window.innerWidth
      : window.innerHeight / 1.9
    this.scene = new THREE.Scene()

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    this.renderer.setClearColor(0xffffff, 0)
    this.renderer.setSize(width, height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)) // reduces edge blur
    this.mount.appendChild(this.renderer.domElement)

    // Camera
    this.camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 1000)
    this.camera.position.z = 20
    this.camera.position.y = 0 // adjusts camera height

    // Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true
    this.controls.enableZoom = false
    this.controls.enablePan = false

    // Lights
    this.scene.add(new THREE.AmbientLight(0xffffff))

    // Simple Box with WireFrame
    this.addModels()

    // Three.js webgl - postprocessing
    this.renderScene()

    // Start animation
    this.start()

    window.addEventListener("resize", () => {
      // Update sizes
      const width = !this.props.isHomepage
        ? 70
        : this.props.isMobileBp && window.innerHeight > window.innerWidth
        ? window.innerWidth
        : window.innerHeight / 1.9
      const height = !this.props.isHomepage
        ? 70
        : this.props.isMobileBp && window.innerHeight > window.innerWidth
        ? window.innerWidth
        : window.innerHeight / 1.9

      // Update camera
      this.camera.aspect = width / height
      this.camera.updateProjectionMatrix()

      // Update renderer
      this.renderer.setSize(width, height)
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    })
  }

  addModels() {
    //Create Material First
    const material = new THREE.MeshPhongMaterial({
      color: 0x00bdff, // red (can also use a CSS color string here)
      //flatShading: true,
      shininess: 150,
      combine: THREE.MixOperation,
      reflectivity: 0.5,
    })
    //Load Object Now and Set Material
    var objLoader = new OBJLoader()
    objLoader.load(
      `https://wbcg-cors-anywhere.herokuapp.com/${this.props.obj}`,
      object => {
        this.objMesh = object
        var objBbox = new THREE.Box3().setFromObject(object) // bounding box created to shift object to be centered at (0, 0, 0) in scene

        object.traverse(function (child) {
          if (child instanceof THREE.Mesh) {
            child.material = material
            child.geometry.translate(0, 0, 0)
          }
        })

        objBbox.setFromObject(object) // Update the bounding box
        this.objMesh.scale.set(
          this.state.objScale,
          this.state.objScale,
          this.state.objScale
        )
        this.scene.add(this.objMesh)
      },
      xhr => {
        console.log((xhr.loaded / xhr.total) * 100 + "% loaded")
      },
      // called when loading has errors
      error => {
        console.log("An error happened" + error)
      }
    )
  }

  componentWillUnmount() {
    this.stop()
    this.mount.removeChild(this.renderer.domElement)
    // window.removeEventListener("resize", this.handleResize, false);
  }

  componentDidUpdate(prevProps) {
    if (this.props.isHomepage !== prevProps.isHomepage) {
      // Update sizes
      const width = !this.props.isHomepage
        ? 70
        : this.props.isMobileBp && window.innerHeight > window.innerWidth
        ? window.innerWidth
        : window.innerHeight / 1.9
      const height = !this.props.isHomepage
        ? 70
        : this.props.isMobileBp && window.innerHeight > window.innerWidth
        ? window.innerWidth
        : window.innerHeight / 1.9

      // Update camera
      this.camera.aspect = width / height
      this.camera.updateProjectionMatrix()

      // Update renderer
      this.renderer.setSize(width, height)
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    }
  }

  start = () => {
    if (!this.frameId) {
      this.frameId = requestAnimationFrame(this.animate)
    }
  }

  stop = () => {
    cancelAnimationFrame(this.frameId)
  }

  animate = () => {
    // -----Step 3--------
    //Rotate Models
    if (this.objMesh) this.objMesh.rotation.y -= 0.008 // update this to change rotation speed

    this.controls.update()
    this.renderScene()
    this.frameId = window.requestAnimationFrame(this.animate)
  }

  renderScene = () => {
    // render the composer to apply the post-processing bloom effect (i.e. glow)
    if (this.renderer) this.renderer.render(this.scene, this.camera)
    // comment out the composer and render the renderer instead if you want to remove the glow effect
    // and render the object with just its material properties
    //if (this.renderer) this.renderer.render(this.scene, this.camera);
  }

  render() {
    return (
      <div
        style={{ width: "100%", height: "100%" }}
        ref={mount => {
          this.mount = mount
        }}
      />
    )
  }
}

const HeaderRadian = ({
  theme = "default",
  bg = 0,
  shadow = "none",
  logo,
  rightImage,
  siteTitle,
  isHomepage,
}) => {
  const isMobileBp = useBreakpoint("sm")

  const Logo = () => (
    <>
      {logo && !isHomepage && (
        <Box width={{ _: 8, md: 9 }} height={{ _: 2, md: 3 }}>
          <Link aria-label="Go to home page link" to="/">
            {logo && (
              <Image
                alternativeText="Header Logo"
                {...logo}
                style={{ height: "100%" }}
                imgStyle={{
                  objectFit: "contain",
                  objectPosition: "left center",
                }}
              />
            )}
          </Link>
        </Box>
      )}
      {(!logo || isHomepage) && (
        <Link aria-label="Go to home page link" to="/">
          <Title
            color="#0F0047"
            fontSize="14px"
            textTransform="uppercase"
            letterSpacing="2px"
            fontWeight="medium"
            variant="h1"
            as="span"
          >
            {siteTitle}
          </Title>
        </Link>
      )}
    </>
  )

  return (
    <ThemeSwitcher theme={isHomepage ? "light" : theme}>
      <StyledHeader
        bg={`background.${bg}`}
        style={
          isHomepage
            ? {
                height: notGatsby
                  ? "87vh" // fudging the header + footer proportions for the Morphic app
                  : isMobileBp
                  ? window.innerHeight // on mobile browsers, 100vh is equal to window.outerHeight by default
                  : "100vh",
              }
            : { height: "80px", zIndex: "header" }
        }
        boxShadow={shadow}
      >
        <Flex
          px={5}
          pt={isHomepage ? 7 : null}
          pb={isHomepage && !notGatsby ? 12 : null}
          flexDirection={isHomepage ? "column" : "row"}
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          height="100%"
        >
          <Logo />
          {rightImage?.url && (
            <Box pb={isHomepage ? null : 2}>
              <ThreeScene
                obj={rightImage?.url}
                isHomepage={isHomepage}
                isMobileBp={isMobileBp}
              />
            </Box>
          )}
          {/* empty box added to center ThreeScene in Homepage flex column*/}
          {isHomepage && !notGatsby && <Box></Box>}
        </Flex>
      </StyledHeader>
    </ThemeSwitcher>
  )
}

export default HeaderRadian

HeaderRadian.strapiProps = {
  logo: PropTypes.shape({
    url: PropTypes.string,
    alternativeText: PropTypes.string,
  }),
  rightImage: PropTypes.shape({
    url: PropTypes.string,
    alternativeText: PropTypes.string,
  }),
}

HeaderRadian.propTypes = {
  ...HeaderRadian.strapiProps,
  siteTitle: PropTypes.string.isRequired,
}

export const query = graphql`
  fragment HeaderRadian on STRAPI__COMPONENT_HEADERS_RADIAN {
    __typename
    logo {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            width: 208
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
    }
    rightImage {
      url
    }
  }
`
